import React, { useState, useEffect } from "react";
import { Doughnut, Bar } from "react-chartjs-2";
import "react-circular-progressbar/dist/styles.css";
import "./UsagePopup.scss";

import { Chart as ChartJS, ArcElement, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from "chart.js";

ChartJS.register(ArcElement, Tooltip, Legend, BarElement, CategoryScale, LinearScale);

const UsagePopup = ({ onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [usageValue, setUsageValue] = useState(0);
  const [usageQuota, setUsageQuota] = useState(0);
  const [usagePercentage, setUsagePercentage] = useState(0);
  const [usageHistory, setUsageHistory] = useState([]);

  useEffect(() => {
    fetchUsageData();
    fetchUsageHistory();
  }, []);

  /**
   * Formats a raw number to euro format by dividing by 1e9
   * and adding the euro symbol.
   * @param {number} value
   * @returns {string} -
   */
  const formatEuro = (value) => {
    return `${(value / 1e9).toFixed(2).replace(".", ",")}€`;
  };

  const fetchUsageData = async () => {
    try {
      setLoading(true);
      setError("");

      const token = localStorage.getItem("admin-token");
      const usageType = "ai_spend";

      const url = `${process.env.REACT_APP_API_URL}/api/config/usage?type=${usageType}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          accept: "application/json",
          authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch usage. Status: ${response.status}`);
      }

      const result = await response.json();
      if (!result.success) {
        setError(result.message || "No usage data found.");
        return;
      }

      const usageArray = result.data.usages;
      if (!usageArray || usageArray.length === 0) {
        setError("No usage data returned.");
        return;
      }

      const { value, quota } = usageArray[0];
      const percent = quota > 0 ? (value / quota) * 100 : 0;
      setUsageValue(value);
      setUsageQuota(quota);
      setUsagePercentage(percent);
    } catch (err) {
      console.error("Error fetching usage data:", err);
      setError("Error fetching usage data.");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsageHistory = async () => {
    try {
      const token = localStorage.getItem("admin-token");
      const usageType = "ai_spend";

      const url = `${process.env.REACT_APP_API_URL}/api/config/usage/history`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          accept: "application/json",
          authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch usage history. Status: ${response.status}`);
      }

      const result = await response.json();
      if (!result.success) {
        console.warn(result.message || "No usage history found.");
        return;
      }

      setUsageHistory(result.usageHistory);
    } catch (err) {
      console.error("Error fetching usage history:", err);
    }
  };

  const getPathColor = (percentage) => {
    if (percentage < 50) return "#4BC0C0";
    if (percentage < 75) return "#FFCE56";
    return "#FF6384";
  };

  const doughnutChartData = {
    labels: ["Utilizado", "Disponível"],
    datasets: [
      {
        data: [usageValue, usageQuota - usageValue],
        backgroundColor: [getPathColor(usagePercentage), "#E0E0E0"],
        hoverBackgroundColor: [getPathColor(usagePercentage), "#CCCCCC"],
        borderWidth: 1,
      },
    ],
  };

  const doughnutChartOptions = {
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          boxWidth: 20,
          padding: 15,
        },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const label = context.label || "";
            const value = context.parsed;
            const formattedValue = formatEuro(value);
            return `${label}: ${formattedValue}`;
          },
        },
      },
    },
    responsive: true,
    maintainAspectRatio: false,
  };

  const barChartData = {
    labels: usageHistory.map((item) => `${item.month}/${item.year}`),
    datasets: [
      {
        label: "Utilização (€)",
        data: usageHistory.map((item) => parseFloat(item.usageValue / 1e9)),
        backgroundColor: usageHistory.map((item) => {
          if (item.percentage < 50) return "rgba(75, 192, 192, 0.6)";
          if (item.percentage < 75) return "rgba(255, 206, 86, 0.6)";
          return "rgba(255, 99, 132, 0.6)"; // Red
        }),
        borderColor: usageHistory.map((item) => {
          if (item.percentage < 50) return "rgba(75, 192, 192, 1)";
          if (item.percentage < 75) return "rgba(255, 206, 86, 1)";
          return "rgba(255, 99, 132, 1)";
        }),
        borderWidth: 1,
      },
    ],
  };

  const barChartOptions = {
    scales: {
      y: {
        beginAtZero: true,
        max: usageQuota / 1e9,
        title: {
          display: true,
          text: "Utilização (€)",
        },
      },
      x: {
        title: {
          display: true,
          text: "Mês/Ano",
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
    },
    responsive: true,
    maintainAspectRatio: false,
  };

  return (
    <div className="usage-popup">
      <div className="usage-popup__overlay" onClick={onClose}></div>
      <div className="usage-popup__content">
        <h2>Utilização</h2>
        <button className="usage-popup__close" onClick={onClose}>
          &times;
        </button>

        {loading && <p>Carregando...</p>}
        {error && <p className="error">{error}</p>}

        {!loading && !error && (
          <>
            <div className="usage-popup__chart">
              <Doughnut data={doughnutChartData} options={doughnutChartOptions} />
            </div>

            <div className="usage-popup__info">
              <p>
                Utilizado: <strong>{formatEuro(usageValue)}</strong>
              </p>
              <p>
                Limite: <strong>{formatEuro(usageQuota)}</strong>
              </p>
              <p>
                Percentagem: <strong>{usagePercentage.toFixed(2)}%</strong>
              </p>
            </div>

            {usageHistory.length > 0 && (
              <div className="usage-popup__history">
                <h3>Histórico dos Últimos 12 Meses</h3>
                <div className="bar-chart-container">
                  <Bar data={barChartData} options={barChartOptions} />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default UsagePopup;
